import type { RaterRelationshipOption } from "~vue/api/generated/reflections";
import { i18n } from "~vue/i18n";
import { type DateFormat, formatDate as localizedDate } from "~vue/utils/formatDate";
import { formatNumber } from "~vue/utils/formatNumber";

export type DateStyle = "default" | "short" | "long";

const DATE_STYLE_FORMATS: Record<DateStyle, DateFormat> = { default: "date", short: "month-day", long: "date-long" };

export function formatDate(iso: string | null | undefined, style: DateStyle = "default"): string {
    return localizedDate(iso, DATE_STYLE_FORMATS[style]);
}

const DAY_MS = 1000 * 60 * 60 * 24;
const RELATIVE_LABEL_DAYS = 7;

function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// Whole days between today and *iso*, or null when it isn't a usable date.
// Both ends are snapped to the start of the day so "tomorrow" is 1 however
// late in the day it is read.
export function daysFromToday(iso: string | null | undefined): number | null {
    if (!iso) {
        return null;
    }
    const target = Date.parse(iso);
    if (!Number.isFinite(target)) {
        return null;
    }
    return Math.round((startOfDay(new Date(target)) - startOfDay(new Date())) / DAY_MS);
}

// Null when the cycle has no deadline — a DRAFT cycle outside a campaign has no
// `closes_at` until launch — so callers omit the label rather than render
// "Closes" with a blank date.
export function closesLabel(iso: string | null | undefined): string | null {
    if (!iso) {
        return null;
    }
    const closesAt = Date.parse(iso);
    if (!Number.isFinite(closesAt)) {
        return null;
    }
    if (closesAt <= Date.now()) {
        return i18n.global.t("reflectionsAdmin.campaign.state.closed");
    }
    const days = daysFromToday(iso) ?? 0;
    if (days === 0) {
        return i18n.global.t("reflections.shared.closes.today");
    }
    if (days === 1) {
        return i18n.global.t("reflections.shared.closes.tomorrow");
    }
    if (days <= RELATIVE_LABEL_DAYS) {
        return i18n.global.t("reflections.shared.closes.inDays", { days }, days);
    }
    const short = formatDate(iso, "short");
    return short ? i18n.global.t("reflectionsAdmin.campaign.closes", { date: short }) : null;
}

export function inviteStateChipState(state: string) {
    switch (state) {
        case "responded":
            return "done";
        case "pending":
            return "pending";
        case "revoked":
        case "expired":
            return "neutral";
        default:
            return "neutral";
    }
}

export function inviteStateLabel(state: string): string {
    switch (state) {
        case "responded":
            return i18n.global.t("reflections.shared.inviteState.submitted");
        case "pending":
            return i18n.global.t("reflections.shared.inviteState.pending");
        case "revoked":
            return i18n.global.t("reflections.shared.inviteState.revoked");
        case "expired":
            return i18n.global.t("reflections.shared.inviteState.expired");
        default:
            return state;
    }
}

export function cycleStateLabel(state: string | undefined): string {
    switch (state) {
        case "draft":
            return i18n.global.t("teamResults.states.draft");
        case "collecting":
            return i18n.global.t("teamResults.states.collecting");
        case "unlocked":
            return i18n.global.t("teamResults.states.unlocked");
        case "closed":
            return i18n.global.t("teamResults.states.closed");
        default:
            return state ?? "";
    }
}

export function cycleStateChipState(state: string | undefined) {
    switch (state) {
        case "unlocked":
            return "done";
        case "collecting":
            return "active";
        case "closed":
            return "neutral";
        default:
            return "pending";
    }
}

// The categories every organization had before they became configurable. A payload
// from a coach_api that predates the field carries no list, and a picker with no
// options strands the rater — fall back to these rather than rendering nothing.
export const DEFAULT_RATER_RELATIONSHIPS: RaterRelationshipOption[] = [
    { value: "peer", label: "Peer" },
    { value: "manager", label: "Manager" },
    { value: "direct", label: "Direct report" },
    { value: "other", label: "Other" },
];

// The default categories carry English labels; a screen that falls back to them
// shows these translated ones instead.
export function raterRelationshipLabel(value: string): string {
    switch (value) {
        case "peer":
            return i18n.global.t("reflections.shared.relationship.peer");
        case "manager":
            return i18n.global.t("reflections.shared.relationship.manager");
        case "direct":
            return i18n.global.t("reflections.shared.relationship.directReport");
        case "other":
            return i18n.global.t("reflections.shared.relationship.other");
        default:
            return DEFAULT_RATER_RELATIONSHIPS.find((option) => option.value === value)?.label ?? value;
    }
}

// coach_api rejects a relationship the organization has since deactivated. The
// admin can change the list while a nomination or rater page is open, so the
// caller re-reads the current categories instead of retrying the stale one.
export function isRelationshipRejected(errorBody: unknown): boolean {
    return typeof errorBody === "object" && errorBody !== null && (errorBody as { code?: string }).code === "invalid_relationship";
}

const RELATIONSHIP_OPTION_BODIES: Record<RaterRelationshipOption["value"], (name: string) => string> = {
    peer: () => i18n.global.t("reflections.shared.relationship.peerBody"),
    manager: (name) => i18n.global.t("reflections.shared.relationship.managerBody", { name }),
    skip_manager: (name) => i18n.global.t("reflections.shared.relationship.skipManagerBody", { name }),
    direct: (name) => i18n.global.t("reflections.shared.relationship.directBody", { name }),
    other: () => i18n.global.t("reflections.shared.relationship.otherBody"),
    self: () => "",
};

// Helper copy under each option on the rater relationship picker.
export function relationshipOptionBody(value: RaterRelationshipOption["value"], subjectFirstName: string): string {
    return RELATIONSHIP_OPTION_BODIES[value](subjectFirstName);
}

const SELF_VS_OTHERS_GAP_THRESHOLD = 0.7;

export function isBigGap(self: number | null, others: number | null): boolean {
    if (self === null || others === null) {
        return false;
    }
    return Math.abs(self - others) >= SELF_VS_OTHERS_GAP_THRESHOLD;
}

export function formatGap(diff: number): string {
    const gap = `${diff > 0 ? "+" : ""}${formatNumber(diff, "one-decimal")}`;
    if (Math.abs(diff) < SELF_VS_OTHERS_GAP_THRESHOLD) {
        return gap;
    }
    return diff > 0 ? i18n.global.t("reflections.shared.gap.youHigher", { gap }) : i18n.global.t("reflections.shared.gap.othersHigher", { gap });
}

// Whether the subject may nudge or remove this rater. Normally only a rater who
// hasn't answered yet, but an organization that hides rater status sends no
// `state`, and gating on it would say who has already responded — so every invite
// the subject can see is actionable there, and the API decides the real outcome.
export function canActOnInvite(cycle: { rater_status_hidden?: boolean } | null | undefined, invite: { state?: string | null }): boolean {
    return cycle?.rater_status_hidden === true || invite.state === "pending";
}

// Tells the submit/load error paths whether a request never reached the server
// (offline, dropped connection) so they can say "your answers are saved — check
// your connection" instead of a scarier "Could not submit". fetch rejects such
// requests with a TypeError and no response; our useFetch wrapper (api/index.ts
// onFetchError) has already renamed every HTTP/parse failure, so a TypeError
// here is a network failure by elimination. Browser message strings vary, so
// they are deliberately not matched.
export function isNetworkError(error: { name?: string } | null | undefined): boolean {
    if (!error) {
        return false;
    }
    return error.name === "TypeError" || globalThis.navigator?.onLine === false;
}
